import React, { useState } from 'react';
import {
    Box,
    TextField,
    MenuItem,
    Button,
    Typography,
    Paper,
    Stack,
    Alert,
    CircularProgress,
    Divider
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { FoodItem } from '../../../services/api';

interface FoodEntryFormProps {
    onAddFood: (food: FoodItem) => void;
}

// Common food items with their typical shelf life in days
const foodShelfLifeDatabase: { [key: string]: number } = {
    'Rice': 365, // 1 year
    'Wheat': 180, // 6 months
    'Flour': 180, // 6 months
    'Sugar': 720, // 2 years
    'Salt': 1825, // 5 years
    'Cooking Oil': 730, // 2 years
    'Lentils': 730, // 2 years
    'Beans': 730, // 2 years
    'Pasta': 730, // 2 years
    'Canned Vegetables': 1095, // 3 years
    'Canned Fruits': 1095, // 3 years
    'Canned Fish': 1095, // 3 years
    'Canned Meat': 1095, // 3 years
    'Milk Powder': 365, // 1 year
    'Baby Formula': 365, // 1 year
    'Cereal': 365, // 1 year
    'Biscuits': 180, // 6 months
    'Nuts': 365, // 1 year
    'Dried Fruits': 365, // 1 year
    'Spices': 730, // 2 years
    'Tea': 730, // 2 years
    'Coffee': 730, // 2 years
    'Bottled Water': 730, // 2 years
    'Energy Bars': 365, // 1 year
};

const units = ['kg', 'liters', 'pieces', 'packets', 'cans', 'bottles'];

const storageConditions = [
    'Dry storage',
    'Cool and dry',
    'Refrigerated',
    'Frozen',
    'Cool and dark'
];

const FoodEntryForm: React.FC<FoodEntryFormProps> = ({ onAddFood }) => {
    const [customFood, setCustomFood] = useState(false);
    const [name, setName] = useState('');
    const [quantity, setQuantity] = useState('');
    const [unit, setUnit] = useState('kg');
    const [expiryDate, setExpiryDate] = useState<Dayjs | null>(null);
    const [location, setLocation] = useState('');
    const [storageCondition, setStorageCondition] = useState('Dry storage');
    const [fetchingShelfLife, setFetchingShelfLife] = useState(false);
    const [shelfLife, setShelfLife] = useState<number | null>(null);
    const [predictionRequested, setPredictionRequested] = useState(false);

    // AI prediction for shelf life
    const predictShelfLife = async () => {
        if (!name) return;

        setFetchingShelfLife(true);
        setPredictionRequested(true);

        try {
            // Simulate an API call to an AI service
            await new Promise(resolve => setTimeout(resolve, 1500));

            // Check our database first
            if (foodShelfLifeDatabase[name]) {
                setShelfLife(foodShelfLifeDatabase[name]);
            } else {
                // For custom foods, we'd call an actual AI API here
                // For now, generate a random shelf life between 30 and 1095 days (3 years)
                const randomShelfLife = Math.floor(Math.random() * (1095 - 30 + 1)) + 30;
                setShelfLife(randomShelfLife);
            }
        } catch (error) {
            console.error('Error predicting shelf life:', error);
        } finally {
            setFetchingShelfLife(false);
        }
    };

    // Calculate and set expiry date based on shelf life
    const calculateExpiryDate = () => {
        if (!shelfLife) return;

        const today = dayjs();
        const calculatedExpiryDate = today.add(shelfLife, 'day');
        setExpiryDate(calculatedExpiryDate);
    };

    // Reset the form
    const resetForm = () => {
        setName('');
        setQuantity('');
        setUnit('kg');
        setExpiryDate(null);
        setLocation('');
        setStorageCondition('Dry storage');
        setShelfLife(null);
        setPredictionRequested(false);
        setCustomFood(false);
    };

    // Handle form submission
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!name || !quantity || !expiryDate || !location) {
            alert('Please fill all required fields');
            return;
        }

        const newItem: FoodItem = {
            id: Date.now().toString(),
            name,
            quantity: parseFloat(quantity),
            unit,
            expiryDate: expiryDate.toISOString(),
            shelfLife: shelfLife || 0,
            location,
            storageCondition
        };

        onAddFood(newItem);
        resetForm();
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Paper sx={{ p: 3 }}>
                <form onSubmit={handleSubmit}>
                    <Stack spacing={3}>
                        <Box>
                            <Typography variant="subtitle1" gutterBottom>
                                Food Item Details
                            </Typography>

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                {!customFood ? (
                                    <TextField
                                        label="Food Item"
                                        select
                                        fullWidth
                                        required
                                        value={name}
                                        onChange={(e) => {
                                            setName(e.target.value);
                                            // Reset shelf life prediction when food item changes
                                            setShelfLife(null);
                                            setPredictionRequested(false);
                                        }}
                                    >
                                        {Object.keys(foodShelfLifeDatabase).map((food) => (
                                            <MenuItem key={food} value={food}>
                                                {food}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                ) : (
                                    <TextField
                                        label="Food Item Name"
                                        fullWidth
                                        required
                                        value={name}
                                        onChange={(e) => {
                                            setName(e.target.value);
                                            // Reset shelf life prediction when food item changes
                                            setShelfLife(null);
                                            setPredictionRequested(false);
                                        }}
                                    />
                                )}

                                <Button
                                    variant="outlined"
                                    onClick={() => setCustomFood(!customFood)}
                                    sx={{ mt: { xs: 1, sm: 0 }, alignSelf: 'flex-start' }}
                                >
                                    {customFood ? 'Select Common Food' : 'Add Custom Food'}
                                </Button>
                            </Stack>

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
                                <TextField
                                    label="Quantity"
                                    type="number"
                                    fullWidth
                                    required
                                    value={quantity}
                                    onChange={(e) => setQuantity(e.target.value)}
                                    inputProps={{ min: 0, step: 0.1 }}
                                />

                                <TextField
                                    label="Unit"
                                    select
                                    fullWidth
                                    required
                                    value={unit}
                                    onChange={(e) => setUnit(e.target.value)}
                                >
                                    {units.map((unit) => (
                                        <MenuItem key={unit} value={unit}>
                                            {unit}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Stack>
                        </Box>

                        <Divider />

                        <Box>
                            <Typography variant="subtitle1" gutterBottom>
                                Storage Information
                            </Typography>

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <TextField
                                    label="Storage Location"
                                    fullWidth
                                    required
                                    value={location}
                                    onChange={(e) => setLocation(e.target.value)}
                                />

                                <TextField
                                    label="Storage Condition"
                                    select
                                    fullWidth
                                    required
                                    value={storageCondition}
                                    onChange={(e) => setStorageCondition(e.target.value)}
                                >
                                    {storageConditions.map((condition) => (
                                        <MenuItem key={condition} value={condition}>
                                            {condition}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Stack>
                        </Box>

                        <Divider />

                        <Box>
                            <Typography variant="subtitle1" gutterBottom>
                                Expiry Information
                            </Typography>

                            <Stack spacing={2}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                                    <Button
                                        variant="contained"
                                        onClick={predictShelfLife}
                                        disabled={!name || fetchingShelfLife}
                                        sx={{ flex: 1 }}
                                    >
                                        {fetchingShelfLife ? (
                                            <CircularProgress size={24} color="inherit" />
                                        ) : (
                                            'AI Predict Shelf Life'
                                        )}
                                    </Button>

                                    <Button
                                        variant="outlined"
                                        onClick={calculateExpiryDate}
                                        disabled={!shelfLife}
                                        sx={{ flex: 1 }}
                                    >
                                        Calculate Expiry Date
                                    </Button>
                                </Stack>

                                {predictionRequested && (
                                    <Box>
                                        {fetchingShelfLife ? (
                                            <Alert severity="info">
                                                Analyzing shelf life for {name}...
                                            </Alert>
                                        ) : shelfLife ? (
                                            <Alert severity="success">
                                                Predicted shelf life for {name}: {shelfLife} days under {storageCondition.toLowerCase()} conditions
                                            </Alert>
                                        ) : (
                                            <Alert severity="error">
                                                Could not predict shelf life. Please try again or enter manually.
                                            </Alert>
                                        )}
                                    </Box>
                                )}

                                <DatePicker
                                    label="Expiry Date"
                                    value={expiryDate}
                                    onChange={(newValue: Dayjs | null) => setExpiryDate(newValue)}
                                    disablePast
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            required: true
                                        }
                                    }}
                                />
                            </Stack>
                        </Box>

                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            fullWidth
                            size="large"
                        >
                            Add to Inventory
                        </Button>
                    </Stack>
                </form>
            </Paper>
        </LocalizationProvider>
    );
};

export default FoodEntryForm; 